import { BookingServiceClient, UserServiceClient, WaitingListServiceClient } from 'fitbeat.api-clients';
import {
  IPost,
  shouldShowJoinLeaderboardTile,
  UserProfile,
  UserWorkoutSession,
} from 'fitbeat.gym-core';
import {
  BookingState,
  IEnabledFeatures,
  IOauthParams,
  MealPreferences,
  NextWorkout,
} from 'fitbeat.models';
import React from 'react';
import { translate } from 'react-i18next';
import {
  Platform, ScrollView, StatusBar, StyleSheet, View,
} from 'react-native';
import { connect } from 'react-redux';
import { enabledFeaturesSelector, PaywallPage } from '../auth';
import { hasAccessToAStudioGymSelector } from '../auth/auth-selectors';
import { openBookingWizardForInviteBooking } from '../Bookings';
import { BookOrJoinWorkoutButton } from '../Bookings/components/BookOrJoinWorkoutButton';
import { getApiConfig } from '../config';
import build from '../config/build';
import { IAppStore } from '../HomeWorkouts';
import { getMealPlan, setNutritionPreference } from '../Nutrition/actions';
import { selectNextXMeals } from '../Nutrition/selectors';
import { closePaywallModalAction } from '../payments/redux/payments-actions';
import { HomeWorkoutReviewModal } from '../Planner/components/HomeWorkoutReviewModal';
import {
  selectWorkoutInviteId,
} from '../Planner/plannerSelectors';
import { GymResolver } from '../services';
import { GoogleAnalytics } from '../services/google-analytics';
import { StrengthAssessmentWizard } from '../StrengthAssessment/StrengthAssessmentWizard';
import Theme from '../theme';
import { AppStateFocusedEvents } from '../ui/components/AppStateFocusedEvents';
import { SkeletonLoader } from '../ui/components/SkeletonLoader';
import { isWeb } from '../utils';
import { createWaitlistServiceClient } from '../utils/apiClientFactory';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';
import { clearDeepLinkParams } from '../utils/notifications/actions';
import { defaultScale } from '../utils/scaling';
import UserDataConsumer from '../utils/UserDataConsumer';
import {
  initialiseWeightTargetSet,
} from '../WeightTarget/actions';
import {
  bookingDashTileSelected,
  dashboardOpened,
  getAllInstructors,
  getWeeklyGoalData,
  getWorkoutTileData,
  nutritionDashTileSelected,
  startBookingFromDashboard,
} from './actions';
import BookingDashTile from './components/BookingDashTile';
import DashboardHeaderSeeMembershipPlans from './components/DashboardHeaderSeeMembershipPlans';
import HelpBlogPosts from './components/HelpBlogPosts';
import InsightBlogPosts from './components/InsightBlogPosts';
import NutritionDashTile from './components/NutritionDashTile';
import { DashboardHeaderStudioWorkoutStarting } from './components/StudioWorkoutStartingHeader';
import { WelcomeTile } from './components/WelcomeTile';
import WorkoutSlider from './components/WorkoutSlider';
import { isNextWorkoutCheckedInStudioWorkoutSelector } from './selectors';
import { IWeeklyGoalData } from './types';
import { setUserGarminWorkoutImportConfig } from '../auth/oauth/actions';

export interface ISummaryDashboardProps {
  hasAccessToAStudioGym: boolean;
  error: string;
  isWorkoutLoading: boolean;
  isNutritionLoading: boolean;
  nutritionData: any;
  nextWorkout: NextWorkout | null;
  totalStartedWorkouts: number;
  invitationCode: string | undefined;
  navigation: any;
  workoutInviteId: number;
  helpBlogs: IPost[];
  deepLinkedRoute: string | null;
  waitingListServiceClient: WaitingListServiceClient;
  user: any;
  influencerId: string;
  isFetchingWeeklyGoalData: boolean;
  weeklyGoalData: IWeeklyGoalData | null;
  workoutSessions: UserWorkoutSession[];
  paywallModalVisible: boolean;
  enabledFeatures: IEnabledFeatures;
  isNextWorkoutCheckedInStudioWorkout: boolean;
  garminAuth: IOauthParams;
  getWorkoutTileData(hideLoader: boolean): void;
  preloadAllInstructors(): void;
  bookingDashTileSelected(): void;
  nutritionDashTileSelected(): void;
  dashboardOpened(): void;
  toggleWeightTargetModal(visible: boolean): void;
  clearDeepLinks(keys: string[]): void;
  openBookingWizardForInviteBooking(): void;
  fetchWeeklyGoalData(): void;
  getMealPlan(): void;
  setNutritionPreference(mealPreference: MealPreferences): void;
  t(key: string, options?: any): string;
  closePaywallModal(): void;
  settingUserGarminWorkoutImportConfig(data: { ouathToken?: string, oauthVerifier?: string }): void;
}

const { isElite } = build.variant;

type dashboardHeader = 'loading' | 'seeMembershipPlans' | 'welcomeTile' | 'studioWorkoutStarting' | 'default';

export class SummaryDashboard extends React.Component<ISummaryDashboardProps> {
  public navigationDidFocus = () => {
    this.loadInitialDataNotPreloadedFromDataProvider();
  };

  public loadInitialDataNotPreloadedFromDataProvider = () => {
    const hideLoader = true;
    this.props.fetchWeeklyGoalData();
    this.props.getMealPlan();
    this.props.getWorkoutTileData(hideLoader);
    this.props.dashboardOpened();
  };

  public componentDidMount() {
    this.props.preloadAllInstructors();
    this.loadInitialDataNotPreloadedFromDataProvider();

    if (!isElite) {
      StatusBar.setHidden(false);
      if (Platform.OS === 'ios') {
        StatusBar.setBarStyle('dark-content');
      }
    }
    this.openBookingWizardForInvite();
  }

  public componentDidUpdate(prevProps) {
    if (this.props.workoutInviteId !== prevProps.workoutInviteId) {
      this.openBookingWizardForInvite();
    } else if (this.props.influencerId !== prevProps.influencerId) {
      this.props.navigation.navigate('InfluencerContainer');
    }
    this.handleDeepLinkRouteUpdate();
  }

  public openBookingWizardForInvite = () => {
    const { workoutInviteId, user } = this.props;
    if (workoutInviteId) {
      this.props.openBookingWizardForInviteBooking();
    }
  };

  public handleDeepLinkRouteUpdate = () => {
    const {
      navigation, deepLinkedRoute, clearDeepLinks, invitationCode, closePaywallModal,
      toggleWeightTargetModal, garminAuth, settingUserGarminWorkoutImportConfig,
    } = this.props;

    if (garminAuth) {
      settingUserGarminWorkoutImportConfig(garminAuth);
      clearDeepLinks(['garminAuth']);
    }

    if (deepLinkedRoute === 'bodyscan') {
      toggleWeightTargetModal(true);
      clearDeepLinks(['route']);
    }

    if (deepLinkedRoute === 'billing-info') {
      navigation.navigate('Billing');
      clearDeepLinks(['route']);
    }

    if (deepLinkedRoute === 'subscription') {
      closePaywallModal();
      navigation.navigate('PaymentFlowNavigator');
    }

    if (invitationCode) {
      navigation.navigate('InvitationCodeWelcome');
    }
  };

  public render() {
    const {
      user: {
        userProfile,
        workoutSessionService,
        userProfileService,
      },
      isWorkoutLoading,
      isNutritionLoading,
      nutritionData,
      nextWorkout,
      navigation,
      totalStartedWorkouts,
      workoutSessions,
      enabledFeatures,
      weeklyGoalData,
      t,
    } = this.props;
    const insightConfig = enabledFeatures?.organisationConfig?.insightConfig;
    const isNewUserWithNoWorkoutsDoneYet = totalStartedWorkouts === 0;
    const isOrgInsightsAvailable = insightConfig?.customInsightsPostType;
    const prioritiseHelp = isNewUserWithNoWorkoutsDoneYet;
    const prioritiseInsights = isOrgInsightsAvailable;
    const prioritizeInsightsBeforeWorkouts = !!insightConfig?.prioritiseBeforeWorkoutSlider;
    const prioritizeInsightsAfterWorkouts = prioritiseInsights && !prioritizeInsightsBeforeWorkouts;

    const shouldShowWorkoutActionButton = (header: dashboardHeader) => header === 'default';

    const headers: { [key in dashboardHeader]: React.ReactNode } = {
      loading: (
        <SkeletonLoader containerStyle={{ height: defaultScale(367) }} />
      ),
      seeMembershipPlans: (
        <DashboardHeaderSeeMembershipPlans t={t} userProfile={userProfile} />
      ),
      welcomeTile: (
        <WelcomeTile
          nextWorkout={nextWorkout}
          userProfile={userProfile}
          navigation={navigation}
          workoutSessionService={workoutSessionService}
          bookings={workoutSessions}
        />
      ),
      studioWorkoutStarting: (
        <DashboardHeaderStudioWorkoutStarting userProfile={userProfile} />
      ),
      default: (
        <BookingDashTile
          bookings={workoutSessions}
          navigation={navigation}
          isWorkoutLoading={isWorkoutLoading}
          userProfile={userProfile}
          workoutSessionService={workoutSessionService}
          bookingDashTileSelected={this.props.bookingDashTileSelected}
          toggleWeightTargetModal={this.props.toggleWeightTargetModal}
        />
      ),
    };

    const headerType = this.getHeaderType();
    const showWorkoutActionButton = shouldShowWorkoutActionButton(headerType);
    return (
      <View style={styles.container}>
        <StrengthAssessmentWizard userProfile={userProfile} />
        <AppStateFocusedEvents
          onScreenFocus={this.navigationDidFocus}
          navigation={navigation}
        />
        <HomeWorkoutReviewModal
          t={t}
          userProfile={userProfile}
          markNotificationsOfTypeAsRead={userProfileService.markNotificationsOfTypeAsRead}
          navigation={navigation}
        />
        <ScrollView>
          {headers[headerType]}
          <>
            {prioritizeInsightsBeforeWorkouts && <InsightBlogPosts t={t} userProfile={userProfile} />}
            {prioritiseHelp && <HelpBlogPosts t={t} userProfile={userProfile} />}
            <WorkoutSlider
              workoutSessions={workoutSessions}
              navigation={navigation}
              userProfile={userProfile}
            />
            {prioritizeInsightsAfterWorkouts && <InsightBlogPosts t={t} userProfile={userProfile} />}
            <NutritionDashTile
              nutritionData={nutritionData}
              navigation={navigation}
              isLoading={isNutritionLoading}
              nutritionDashTileSelected={this.props.nutritionDashTileSelected}
              user={this.props.user}
              userProfile={userProfile}
            />
            {!prioritiseHelp && <HelpBlogPosts t={t} userProfile={userProfile} />}
            {!prioritizeInsightsAfterWorkouts && !prioritizeInsightsBeforeWorkouts
              && <InsightBlogPosts t={t} userProfile={userProfile} />}
            {showWorkoutActionButton && <View style={styles.spacer} />}
          </>
        </ScrollView>
        {showWorkoutActionButton && (
          <View>
            <BookOrJoinWorkoutButton
              workoutSessions={workoutSessions}
              userProfile={userProfile}
              workoutSessionService={workoutSessionService}
              t={t}
              navigation={navigation}
              buttonContainer={styles.workoutButtonContainer}
              buttonStyles={styles.workoutButtonStyles}
            />
          </View>
        )}
      </View>
    );
  }

  private getHeaderType = (): dashboardHeader => {
    const {
      user: {
        userProfile,
      },
      totalStartedWorkouts,
      workoutSessions,
      isFetchingWeeklyGoalData,
      hasAccessToAStudioGym,
      isNextWorkoutCheckedInStudioWorkout,
      weeklyGoalData,
    } = this.props;
    const noWorkoutsStarted = totalStartedWorkouts === 0;
    const noWorkoutsBooked = workoutSessions.length === 0;
    const gym = GymResolver.getByName(userProfile.gymName);

    let headerType: dashboardHeader = 'default';

    if (totalStartedWorkouts == null || (isFetchingWeeklyGoalData && totalStartedWorkouts > 0)) {
      return 'loading';
    }
    if (weeklyGoalData === null) {
      return 'loading';
    }

    const canOptIn = weeklyGoalData?.primaryLeaderboard
      ? shouldShowJoinLeaderboardTile(weeklyGoalData.primaryLeaderboard)
      : false;

    const shouldSelectPlanOnExternalPage = !gym.shouldUseInAppPurchaseForMembership;
    if (!UserProfile.hasActivePlan(userProfile) && shouldSelectPlanOnExternalPage) {
      return 'seeMembershipPlans';
    }

    if (noWorkoutsStarted && noWorkoutsBooked) {
      headerType = 'welcomeTile';
    }

    if (canOptIn) {
      headerType = 'default';
    }

    if (
      isNextWorkoutCheckedInStudioWorkout
    ) {
      return 'studioWorkoutStarting';
    }

    return headerType;
  };
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: Theme.GREY_LIGHT,
    flex: 1,
  },
  tileStyle: {
    backgroundColor: 'transparent',
  },
  spacer: {
    height: defaultScale(60),
  },
  workoutButtonContainer: {
    paddingHorizontal: defaultScale(12),
    width: '100%',
    position: 'absolute',
    bottom: defaultScale(9),
  },
  workoutButtonStyles: {
    shadowColor: Platform.OS === 'android' ? Theme.BLACK : 'rgba(0, 0, 0, 0.37)',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 10,
    borderRadius: defaultScale(3),
    elevation: Platform.OS === 'android' ? 20 : 0,
    ...isWeb() ? { boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.37)' } : {},
  },
});

const mapStateToProps = (state: IAppStore) => {
  const {
    isWorkoutLoading,
    nutritionError,
    requiresNutritionRating,
  } = state.summaryDashboard.view;
  const { totalStartedWorkouts } = state.summaryDashboard.data;
  const {
    route,
    invitationCode,
    influencerId,
    garminAuth,
  } = state.deepLinkParamaters;
  const { openInstantWorkout } = state.auth.signup;
  const nextWorkout = state.planner.workoutSessions ? state.planner.workoutSessions[0] : null;
  return {
    nutritionError,
    isWorkoutLoading,
    isNutritionLoading: state.nutrition.isLoading,
    nutritionData: selectNextXMeals(state),
    nextWorkout,
    workoutInviteId: selectWorkoutInviteId(state),
    requiresNutritionRating,
    openInstantWorkout,
    totalStartedWorkouts,
    deepLinkedRoute: route,
    invitationCode,
    workoutSessions: state.planner.workoutSessions.filter(
      (session) => session.bookingState !== BookingState.ProvisionalBooking,
    ),
    helpBlogs: state.summaryDashboard.data.helpBlogs,
    isFetchingWeeklyGoalData: state.summaryDashboard.view.isFetchingWeeklyGoalData,
    influencerId,
    paywallModalVisible: state.payments.paywallModalVisible,
    enabledFeatures: enabledFeaturesSelector(state),
    hasAccessToAStudioGym: hasAccessToAStudioGymSelector(state),
    isNextWorkoutCheckedInStudioWorkout: isNextWorkoutCheckedInStudioWorkoutSelector(state),
    weeklyGoalData: !state.summaryDashboard.view.weeklyGoalDataError
      ? state.summaryDashboard.data.weeklyGoalData
      : {
        completedMinutes: '-',
        scheduledMinutes: 0,
        totalTargetMinutes: '-',
      },
    garminAuth,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const userServiceClient = new UserServiceClient(
    getApiConfig(),
    fetch,
    console.log,
    new AuthTokenBuilder(),
    GoogleAnalytics,
  );
  const bookingServiceClient = new BookingServiceClient(getApiConfig(), fetch, new AuthTokenBuilder());
  const waitingListServiceClient = createWaitlistServiceClient();
  const { user: { nutritionService, userProfile } } = ownProps;

  return {
    getWorkoutTileData: (hideLoader: boolean) => dispatch(getWorkoutTileData(userServiceClient, hideLoader)),
    preloadAllInstructors: () => dispatch(getAllInstructors(bookingServiceClient)),
    bookingDashTileSelected: () => {
      dispatch(bookingDashTileSelected());
      dispatch(startBookingFromDashboard());
    },
    nutritionDashTileSelected: () => dispatch(nutritionDashTileSelected()),
    toggleWeightTargetModal: () => dispatch(initialiseWeightTargetSet(userProfile)),
    fetchWeeklyGoalData: () => dispatch(getWeeklyGoalData(userServiceClient)),
    getMealPlan: () => dispatch(getMealPlan(nutritionService)),
    setNutritionPreference: (nutritionPreference) => dispatch(setNutritionPreference(nutritionPreference)),
    dashboardOpened: () => dispatch(
      dashboardOpened(bookingServiceClient, userServiceClient, waitingListServiceClient, userProfile),
    ),
    clearDeepLinks: (keys) => dispatch(clearDeepLinkParams(keys)),
    openBookingWizardForInviteBooking: (timeToBook) => dispatch(
      openBookingWizardForInviteBooking(userProfile, timeToBook),
    ),
    closePaywallModal: () => dispatch(closePaywallModalAction()),
    settingUserGarminWorkoutImportConfig: (data: IOauthParams) => dispatch(
      setUserGarminWorkoutImportConfig(userServiceClient, data),
    ),
  };
};

const Dashboard = UserDataConsumer(connect(mapStateToProps, mapDispatchToProps)(SummaryDashboard));
export default translate(['bookings', 'dashboard'], { wait: true })(Dashboard);
